import type { MouseEvent } from "react";
import {
  ChevronRight,
  FileSpreadsheet,
  FileText,
  Folder,
  MoreHorizontal,
} from "lucide-react";

import { Checkbox, ScrollArea, Table } from "@/components/ui";
import { VaultFileCard } from "@/components/vault/VaultFileCard";
import { VaultFolderCard } from "@/components/vault/VaultFolderCard";
import type { VaultFileItem, VaultFolderNode } from "@/types/vault.types";
import type { VaultViewMode } from "@/types/vaultNav.types";
import { cn } from "@/lib/utils";

function getFileIconStyle(fileIcon: VaultFileItem["fileIcon"]) {
  switch (fileIcon) {
    case "pdf":
      return { icon: FileText, color: "text-red-400", bg: "bg-red-500/10" };
    case "docx":
      return { icon: FileText, color: "text-blue-400", bg: "bg-blue-500/10" };
    case "xlsx":
      return { icon: FileSpreadsheet, color: "text-green-400", bg: "bg-green-500/10" };
    default:
      return { icon: FileText, color: "text-gray-400", bg: "bg-gray-500/10" };
  }
}

interface VaultFileGridProps {
  folders: VaultFolderNode[];
  files: VaultFileItem[];
  viewMode: VaultViewMode;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onFolderOpen: (folderId: string) => void;
  onFileOpen: (file: VaultFileItem) => void;
  selectedFileId?: string | null;
}

export function VaultFileGrid({
  folders,
  files,
  viewMode,
  selectedIds,
  onToggleSelect,
  onFolderOpen,
  onFileOpen,
  selectedFileId,
}: VaultFileGridProps) {
  const empty = folders.length === 0 && files.length === 0;

  if (empty) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-16 text-center text-muted-foreground">
        <Folder className="mb-3 size-12 opacity-20" aria-hidden="true" />
        <p className="text-sm font-medium">No files here yet</p>
        <p className="mt-1 text-xs">Drag files in or use New to add documents.</p>
      </div>
    );
  }

  if (viewMode === "list") {
    return (
      <ScrollArea className="min-h-0 flex-1">
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head className="w-10" />
              <Table.Head>Name</Table.Head>
              <Table.Head className="w-28">Size</Table.Head>
              <Table.Head className="w-32">Modified</Table.Head>
              <Table.Head className="w-10" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {folders.map((folder) => (
              <Table.Row
                key={folder.id}
                className="cursor-pointer"
                onClick={() => onFolderOpen(folder.id)}
              >
                <Table.Cell onClick={(e: MouseEvent) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedIds.has(folder.id)}
                    onCheckedChange={() => onToggleSelect(folder.id)}
                    aria-label={`Select ${folder.name}`}
                  />
                </Table.Cell>
                <Table.Cell>
                  <div className="flex items-center gap-2">
                    <Folder className="size-4 text-amber-400" aria-hidden="true" />
                    <span className="font-medium">{folder.name}</span>
                  </div>
                </Table.Cell>
                <Table.Cell className="text-muted-foreground">—</Table.Cell>
                <Table.Cell className="text-muted-foreground">{folder.modified ?? "—"}</Table.Cell>
                <Table.Cell>
                  <ChevronRight className="size-4 text-muted-foreground" />
                </Table.Cell>
              </Table.Row>
            ))}
            {files.map((file) => {
              const { icon: Icon, color, bg } = getFileIconStyle(file.fileIcon);
              return (
                <Table.Row
                  key={file.id}
                  className={cn("cursor-pointer", selectedFileId === file.id && "bg-primary/5")}
                  onClick={() => onFileOpen(file)}
                >
                  <Table.Cell onClick={(e: MouseEvent) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.has(file.id)}
                      onCheckedChange={() => onToggleSelect(file.id)}
                      aria-label={`Select ${file.name}`}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex items-center gap-2">
                      <div className={cn("flex size-7 items-center justify-center rounded-md", bg)}>
                        <Icon className={cn("size-3.5", color)} aria-hidden="true" />
                      </div>
                      <span className="truncate">{file.name}</span>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="text-muted-foreground">{file.sizeFormatted}</Table.Cell>
                  <Table.Cell className="text-muted-foreground">{file.date}</Table.Cell>
                  <Table.Cell>
                    <MoreHorizontal className="size-4 text-muted-foreground" aria-hidden="true" />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </ScrollArea>
    );
  }

  return (
    <ScrollArea className="min-h-0 flex-1 px-5 py-4">
      {folders.length > 0 && (
        <section className="mb-6">
          <h3 className="mb-2.5 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
            Folders {folders.length}
          </h3>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {folders.map((folder) => (
              <VaultFolderCard
                key={folder.id}
                folder={folder}
                selected={selectedIds.has(folder.id)}
                onOpen={() => onFolderOpen(folder.id)}
                onSelect={() => onToggleSelect(folder.id)}
              />
            ))}
          </div>
        </section>
      )}
      {files.length > 0 && (
        <section>
          <h3 className="mb-2.5 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
            Files {files.length}
          </h3>
          <div className="grid gap-2.5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {files.map((file) => (
              <VaultFileCard
                key={file.id}
                file={file}
                selected={selectedIds.has(file.id)}
                onOpen={() => onFileOpen(file)}
                onSelect={() => onToggleSelect(file.id)}
              />
            ))}
          </div>
        </section>
      )}
    </ScrollArea>
  );
}
